/**
 * VT — Organização de Cores. Closed canonical color taxonomy.
 *
 * Every raw color string ("Cloud Dancer", "Grind") resolves to at most one of
 * these canonical buckets (or stays unmapped → shown as "Outras"). The id is the
 * stable value persisted in `ProductVariant.colorBaseId`,
 * `StoreColorMapping.baseColorId` and `GlobalColorMapping.baseColorId`. The hex
 * is the deterministic swatch shown in the filter/bolinha; `labelKey` is the
 * i18n key (pt-BR/en-US) for the human label.
 */

export type BaseColor = {
  id: string;
  labelKey: string;
  hex: string;
  /** Rendered with a multicolor gradient swatch instead of a flat hex. */
  gradient?: boolean;
  /** Near-white swatches need a visible border on light backgrounds. */
  needsBorder?: boolean;
};

export const BASE_COLORS: readonly BaseColor[] = [
  { id: 'black', labelKey: 'baseColors.black', hex: '#000000' },
  { id: 'white', labelKey: 'baseColors.white', hex: '#ffffff', needsBorder: true },
  { id: 'off-white', labelKey: 'baseColors.offWhite', hex: '#f3efe3', needsBorder: true },
  { id: 'gray', labelKey: 'baseColors.gray', hex: '#9ca3af' },
  { id: 'beige', labelKey: 'baseColors.beige', hex: '#d8c3a5' },
  { id: 'brown', labelKey: 'baseColors.brown', hex: '#7b4a2b' },
  { id: 'red', labelKey: 'baseColors.red', hex: '#dc2626' },
  { id: 'pink', labelKey: 'baseColors.pink', hex: '#ec4899' },
  { id: 'orange', labelKey: 'baseColors.orange', hex: '#f97316' },
  { id: 'yellow', labelKey: 'baseColors.yellow', hex: '#facc15' },
  { id: 'green', labelKey: 'baseColors.green', hex: '#16a34a' },
  { id: 'blue', labelKey: 'baseColors.blue', hex: '#2563eb' },
  { id: 'purple', labelKey: 'baseColors.purple', hex: '#7c3aed' },
  { id: 'gold', labelKey: 'baseColors.gold', hex: '#d4af37' },
  { id: 'silver', labelKey: 'baseColors.silver', hex: '#c0c0c0', needsBorder: true },
  { id: 'multicolor', labelKey: 'baseColors.multicolor', hex: '#a3a3a3', gradient: true },
] as const;

/** Synthetic bucket id for raw colors that have no canonical mapping yet. */
export const OTHER_COLOR_ID = 'outras';
export const OTHER_COLOR_LABEL_KEY = 'baseColors.outras';
/** Neutral swatch shown for the "Outras" bucket. */
export const OTHER_COLOR_HEX = '#d4d4d4';

const BASE_COLOR_BY_ID = new Map<string, BaseColor>(
  BASE_COLORS.map((color) => [color.id, color])
);

export function isBaseColorId(value: string): boolean {
  return BASE_COLOR_BY_ID.has(value);
}

export function getBaseColor(id: string | null | undefined): BaseColor | null {
  if (!id) return null;
  return BASE_COLOR_BY_ID.get(id) ?? null;
}

/** Swatch hex for a base color id, falling back to the "Outras" neutral. */
export function baseColorHex(id: string | null | undefined): string {
  return getBaseColor(id)?.hex ?? OTHER_COLOR_HEX;
}
